import {
  ClientEvent,
  EventType,
  RelationType,
  RoomEvent,
  type MatrixClient,
  type MatrixEvent,
  type Room,
} from 'matrix-js-sdk';

/**
 * Segundo ✓ ("entregue"). O mensageiro só conhece "lida"; a entrega é um aviso
 * curto, fora da conversa, de que a mensagem chegou ao aparelho de quem recebe:
 * - o app de quem recebeu avisa assim que a mensagem chega nele;
 * - com o app fechado, o servidor avisa quando entregou o aviso ao aparelho.
 * O aviso leva só os identificadores da conversa e da mensagem.
 */
const DELIVERED_TYPE = 'net.secretarios.delivered';
const SERVICE_ACCOUNT = 'secretario-ops';
const STORAGE_KEY = 'secretario.entregues.v1';
const KEEP = 800;
const FRESH_MS = 10 * 60 * 1000;
const INCOMING_TYPES: string[] = [
  EventType.RoomMessage,
  EventType.RoomMessageEncrypted,
  EventType.Sticker,
];

let delivered: Set<string> | undefined;
const listeners = new Set<() => void>();

function load(): Set<string> {
  if (delivered) return delivered;
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '[]') as unknown;
    delivered = new Set(Array.isArray(stored) ? stored.map(String) : []);
  } catch {
    delivered = new Set();
  }
  return delivered;
}

function remember(ids: string[]): void {
  const known = load();
  let changed = false;
  for (const id of ids) {
    if (id && !known.has(id)) {
      known.add(id);
      changed = true;
    }
  }
  if (!changed) return;
  const kept = [...known].slice(-KEEP);
  delivered = new Set(kept);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
  } catch {
    /* sem armazenamento: vale até fechar o app */
  }
  for (const listener of listeners) listener();
}

export function isDelivered(eventId: string | null): boolean {
  return !!eventId && load().has(eventId);
}

export function subscribeDelivery(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const localpartOf = (userId: string) => userId.slice(1).split(':')[0] ?? '';
const domainOf = (userId: string) => userId.slice(userId.indexOf(':') + 1);

export function watchDelivery(client: MatrixClient): () => void {
  const me = client.getUserId() ?? '';

  // Aviso de entrega chegando: vale de quem está na conversa ou do próprio serviço.
  const onToDevice = (event: MatrixEvent) => {
    if (event.getType() !== DELIVERED_TYPE) return;
    const sender = event.getSender() ?? '';
    const content = event.getContent() as { room_id?: unknown; event_ids?: unknown };
    const roomId = typeof content.room_id === 'string' ? content.room_id : '';
    const ids = Array.isArray(content.event_ids)
      ? content.event_ids.filter((id): id is string => typeof id === 'string').slice(0, 50)
      : [];
    if (!roomId || ids.length === 0) return;
    const fromService =
      localpartOf(sender) === SERVICE_ACCOUNT && domainOf(sender) === domainOf(me);
    const fromMember = client.getRoom(roomId)?.getMember(sender)?.membership === 'join';
    if (fromService || fromMember) remember(ids);
  };

  // Mensagem chegando aqui: avisa quem enviou (um aviso por conversa, em lote).
  const pending = new Map<string, Map<string, string[]>>();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const flush = () => {
    timer = undefined;
    for (const [sender, rooms] of pending) {
      for (const [roomId, ids] of rooms) {
        void client
          .sendToDevice(
            DELIVERED_TYPE,
            new Map([[sender, new Map([['*', { room_id: roomId, event_ids: ids }]])]]),
          )
          .catch(() => {});
      }
    }
    pending.clear();
  };
  const onTimeline = (
    event: MatrixEvent,
    room: Room | undefined,
    toStartOfTimeline: boolean | undefined,
    _removed: boolean,
    data: { liveEvent?: boolean },
  ) => {
    if (!room || toStartOfTimeline || !data.liveEvent) return;
    if (!INCOMING_TYPES.includes(event.getType())) return;
    const sender = event.getSender();
    const id = event.getId();
    if (!sender || !id || sender === me) return;
    if (event.getRelation()?.rel_type === RelationType.Replace) return;
    if (Date.now() - event.getTs() > FRESH_MS) return;
    const rooms = pending.get(sender) ?? new Map<string, string[]>();
    rooms.set(room.roomId, [...(rooms.get(room.roomId) ?? []), id]);
    pending.set(sender, rooms);
    timer ??= setTimeout(flush, 700);
  };

  client.on(ClientEvent.ToDeviceEvent, onToDevice);
  client.on(RoomEvent.Timeline, onTimeline);
  return () => {
    client.off(ClientEvent.ToDeviceEvent, onToDevice);
    client.off(RoomEvent.Timeline, onTimeline);
    clearTimeout(timer);
  };
}
